<script lang="ts" setup generic="T extends { name: string, logoSrc?: string }">
import { Avatar, AvatarFallback, AvatarImage } from '@forgd/ui-kit/avatar'
import { Skeleton } from '@forgd/ui-kit/skeleton'

const props = withDefaults(defineProps<{
  items: T[]
  label: string
  shortLabel?: string
  logoFallback: string
  loading?: boolean
}>(), {
  loading: false,
})

defineSlots<{
  value: (props: { item: T }) => any
}>()

const SKELETON_COUNT = 10
const skeletonWidths = ['w-16', 'w-20', 'w-14', 'w-24']

// Marquee (and its duplicated second pass) only when the items overflow the
// bar; otherwise each item renders once, statically.
const containerEl = ref<HTMLElement | null>(null)
const firstPassEl = ref<HTMLElement | null>(null)
const isOverflowing = ref(false)
const marqueeDuration = ref('0s')
const MARQUEE_PX_PER_SECOND = 27
let resizeObserver: ResizeObserver | null = null

async function measureOverflow() {
  const container = containerEl.value
  const firstPass = firstPassEl.value
  if (!container || !firstPass) {
    isOverflowing.value = false
    return
  }
  isOverflowing.value = firstPass.scrollWidth > container.clientWidth
  if (isOverflowing.value) {
    await nextTick()
    if (containerEl.value !== container || firstPassEl.value !== firstPass) {
      return
    }
    marqueeDuration.value = `${firstPass.scrollWidth / MARQUEE_PX_PER_SECOND}s`
  }
}

onMounted(() => {
  void measureOverflow()
  resizeObserver = new ResizeObserver(() => void measureOverflow())
  if (containerEl.value) {
    resizeObserver.observe(containerEl.value)
  }
  if (firstPassEl.value) {
    resizeObserver.observe(firstPassEl.value)
  }
  window.addEventListener('resize', measureOverflow)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  window.removeEventListener('resize', measureOverflow)
})

// The items only exist in the DOM once loading is done, so both matter here.
watch(() => [props.items, props.loading], async () => {
  await nextTick()
  await measureOverflow()
})
</script>

<template>
  <div
    class="flex h-11 items-center gap-5 border-b border-default bg-secondary-subtle py-2 pl-5"
  >
    <p class="shrink-0 text-2xs whitespace-nowrap text-secondary">
      <template v-if="shortLabel">
        <span class="md:hidden">{{ shortLabel }}</span>
        <span class="hidden md:inline">{{ label }}</span>
      </template>
      <template v-else>
        {{ label }}
      </template>
    </p>
    <div
      ref="containerEl"
      class="min-w-0 flex-1 overflow-hidden"
      :class="{ 'pr-ticker-fade': isOverflowing }"
    >
      <div v-if="loading" class="flex items-center gap-5">
        <div
          v-for="n in SKELETON_COUNT"
          :key="n"
          class="flex shrink-0 items-center gap-1"
        >
          <Skeleton class="size-5 shrink-0 rounded-full" />
          <Skeleton class="h-3" :class="skeletonWidths[n % skeletonWidths.length]" />
        </div>
      </div>
      <div v-else class="flex w-max items-center" :class="{ 'pr-ticker-track': isOverflowing }">
        <!-- The second pass is decorative — it makes the marquee loop seamless -->
        <div
          v-for="pass in (isOverflowing ? 2 : 1)"
          :key="pass"
          :ref="(el) => { if (pass === 1) firstPassEl = el as HTMLElement | null }"
          class="flex items-center gap-5"
          :class="{ 'pr-5': isOverflowing }"
          :aria-hidden="pass === 2 || undefined"
        >
          <div
            v-for="(item, index) in items"
            :key="index"
            class="flex shrink-0 items-center gap-1"
          >
            <Avatar class="size-5 border border-default bg-white">
              <AvatarImage
                v-if="item.logoSrc"
                :src="item.logoSrc"
                :alt="item.name"
              />
              <AvatarFallback class="bg-white p-0">
                <img :src="logoFallback" :alt="item.name" class="size-full object-cover">
              </AvatarFallback>
            </Avatar>
            <span class="text-2xs font-semibold whitespace-nowrap text-primal">
              {{ item.name }}
            </span>
            <slot name="value" :item="item" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pr-ticker-fade {
  -webkit-mask-image: linear-gradient(to right, transparent 0, black 24px, black calc(100% - 64px), transparent 100%);
  mask-image: linear-gradient(to right, transparent 0, black 24px, black calc(100% - 64px), transparent 100%);
}

.pr-ticker-track {
  animation: pr-ticker-scroll v-bind(marqueeDuration) linear infinite;
}

/* Stops on hover so a name can be read. */
.pr-ticker-fade:hover .pr-ticker-track {
  animation-play-state: paused;
}

@keyframes pr-ticker-scroll {
  from {
    transform: translateX(0);
  }

  to {
    /* Each pass is exactly half the track (items + trailing pr-5 gap). */
    transform: translateX(-50%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .pr-ticker-track {
    animation: none;
  }
}
</style>
